/**
 * UserProfileHeader Component
 * Displays profile image, name, user type, and status
 */

import React from "react";
import { Link } from "react-router-dom";
import profile_image from "../../../../../images/user/Default_pfp.png";
import userTypeName from "../../../../../constants/userType";

// Skeleton loader styles
const skeletonStyle = {
  backgroundColor: "#e5e7eb",
  borderRadius: "4px",
  animation: "pulse 1.5s ease-in-out infinite",
};

// Profile Image Skeleton (circular)
const ProfileImageSkeleton = () => (
  <div className="profile-image">
    <div
      style={{
        width: "107px",
        height: "107px",
        borderRadius: "50%",
        ...skeletonStyle,
      }}
    />
  </div>
);

// Profile Name Skeleton
const ProfileNameSkeleton = () => (
  <div className="profile-name-sec">
    <div
      style={{
        width: "180px",
        height: "24px",
        ...skeletonStyle,
        marginBottom: "4px",
      }}
    />
  </div>
);

// Profile Type Skeleton
const ProfileTypeSkeleton = () => (
  <div className="profile-type">
    <div
      style={{
        width: "100px",
        height: "19px",
        ...skeletonStyle,
      }}
    />
  </div>
);

// Status Button Skeleton
const StatusButtonSkeleton = () => (
  <div
    style={{
      width: "67px",
      height: "31px",
      borderRadius: "100px",
      ...skeletonStyle,
    }}
  />
);

// Upload Photo Link Skeleton
const UploadPhotoSkeleton = () => (
  <span
    style={{
      width: "100px",
      height: "18px",
      display: "inline-block",
      ...skeletonStyle,
    }}
  />
);

// Edit Icon Skeleton
const EditIconSkeleton = () => (
  <div
    style={{
      width: "29px",
      height: "29px",
      borderRadius: "50%",
      ...skeletonStyle,
    }}
  />
);

const UserProfileHeader = ({
  isLoading,
  userView,
  firstName,
  middleName,
  lastName,
  companyName,
  companyFirstName,
  companyLastName,
  userType,
  user,
  userCategory,
  userCategoryName,
  uploadedImageUrl,
  removeProfileImage,
  fileSelectIsLoading,
  dropdownVisible,
  dropdownRef,
  isSubmitLoading,
  onProfilePicChange,
  onDropdownToggle,
  onRemoveProfileImageChange,
  StatusChangeActive,
  StatusChangeInactive,
  SUPER_ADMIN,
  ADMIN,
}) => {
  return (
    <>
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
      <div className="profile-photo-outer">
        <div className="profile-photo">
          <div className="profile-photo-inner">
            <div className="profile-photo-sec">
              {((userView.profile_image !== "null" && userView.profile_image !== null) || uploadedImageUrl) && !isLoading && !removeProfileImage ? (
                <div className="profile-photo-sec-edit-outer">
                  <div className="pic-edit picart" onClick={!fileSelectIsLoading ? onDropdownToggle : undefined}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="29" height="29" viewBox="0 0 29 29" fill="none">
                      <circle cx="14.5" cy="14.5" r="14.5" fill="#6479F8" />
                      <path d="M22.7194 10.1524L19.2559 6.66847C19.2063 6.61849 19.1405 6.59104 19.0701 6.59104C18.9998 6.59104 18.9344 6.6185 18.8847 6.66848L16.8328 8.73279L16.8303 8.74018L16.7894 8.77644L8.86392 16.7497C8.82719 16.7866 8.80235 16.8331 8.7922 16.8838L7.91485 21.2513C7.89736 21.3382 7.9243 21.4273 7.98658 21.4903C8.03626 21.5399 8.10204 21.5673 8.17236 21.5673C8.18986 21.5673 8.207 21.5656 8.2231 21.5624L12.5654 20.6792C12.6164 20.669 12.6623 20.644 12.6987 20.6074L22.7194 10.5259C22.7691 10.4762 22.7961 10.4097 22.7961 10.339C22.7961 10.2682 22.7688 10.2021 22.7194 10.1524ZM12.3831 20.1777L8.50718 20.9662L9.29043 17.0668L10.4345 15.9158L13.5271 19.0271L12.3831 20.1777ZM13.898 18.6536L10.8053 15.5427L16.9801 9.33077L20.0724 12.4417L13.8976 18.6536L13.898 18.6536ZM20.4439 12.0682L17.3516 8.95733L19.0702 7.22844L22.1628 10.3393L20.4439 12.0682Z" fill="white" stroke="white" strokeWidth="0.4" />
                    </svg>
                  </div>

                  {dropdownVisible && (
                    <div className="dropdown-edit-profile" ref={dropdownRef}>
                      <div className="profile-pic-edit">
                        <Link 
                          className="pic-edit" 
                          onClick={!fileSelectIsLoading ? onProfilePicChange : undefined}
                          style={{
                            pointerEvents: fileSelectIsLoading ? "none" : "auto",
                            cursor: fileSelectIsLoading ? "not-allowed" : "pointer",
                            opacity: fileSelectIsLoading ? 0.6 : 1,
                          }}
                        >
                          {fileSelectIsLoading ? (
                            <UploadPhotoSkeleton />
                          ) : (
                            <span>Upload Photo</span>
                          )}
                        </Link>
                      </div>
                      <div className="remove-pic">
                        <label htmlFor="delete1">
                          <input
                            type="checkbox"
                            checked={removeProfileImage}
                            onChange={() => onRemoveProfileImageChange(!removeProfileImage)}
                            id="delete1"
                            hidden
                          />
                          Delete Photo
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="profile-pic-edit">
                  <Link 
                    className="pic-edit" 
                    onClick={!fileSelectIsLoading ? onProfilePicChange : undefined}
                    style={{
                      pointerEvents: fileSelectIsLoading ? "none" : "auto",
                      cursor: fileSelectIsLoading ? "not-allowed" : "pointer",
                      opacity: fileSelectIsLoading ? 0.6 : 1,
                    }}
                  >
                    {fileSelectIsLoading ? (
                      <EditIconSkeleton />
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="29" height="29" viewBox="0 0 29 29" fill="none">
                        <circle cx="14.5" cy="14.5" r="14.5" fill="#6479F8" />
                        <path d="M22.7194 10.1524L19.2559 6.66847C19.2063 6.61849 19.1405 6.59104 19.0701 6.59104C18.9998 6.59104 18.9344 6.6185 18.8847 6.66848L16.8328 8.73279L16.8303 8.74018L16.7894 8.77644L8.86392 16.7497C8.82719 16.7866 8.80235 16.8331 8.7922 16.8838L7.91485 21.2513C7.89736 21.3382 7.9243 21.4273 7.98658 21.4903C8.03626 21.5399 8.10204 21.5673 8.17236 21.5673C8.18986 21.5673 8.207 21.5656 8.2231 21.5624L12.5654 20.6792C12.6164 20.669 12.6623 20.644 12.6987 20.6074L22.7194 10.5259C22.7691 10.4762 22.7961 10.4097 22.7961 10.339C22.7961 10.2682 22.7688 10.2021 22.7194 10.1524ZM12.3831 20.1777L8.50718 20.9662L9.29043 17.0668L10.4345 15.9158L13.5271 19.0271L12.3831 20.1777ZM13.898 18.6536L10.8053 15.5427L16.9801 9.33077L20.0724 12.4417L13.8976 18.6536L13.898 18.6536ZM20.4439 12.0682L17.3516 8.95733L19.0702 7.22844L22.1628 10.3393L20.4439 12.0682Z" fill="white" stroke="white" strokeWidth="0.4" />
                      </svg>
                    )}
                  </Link>
                </div>
              )}

              {isLoading ? (
                <ProfileImageSkeleton />
              ) : (
                removeProfileImage ? (
                  <div className="profile-image">
                    <img src={profile_image} alt="Default Profile" />
                  </div>
                ) : uploadedImageUrl ? (
                  <div className="profile-image">
                    <img src={uploadedImageUrl} alt="Uploaded Profile " />
                  </div>
                ) : (
                  <div className="profile-image">
                    {userView.profile_image !== "null" && userView.profile_image !== null ? (
                      <img
                        src={`/uploads/orgiProfileImage/${userView.profile_image}`}
                        alt="User Profile "
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/uploads/lowProfileImage/user_default.png';
                        }}
                      />
                    ) : (
                      <img src={profile_image} alt="User Profile " />
                    )}
                  </div>
                )
              )}
            </div>
            <div className="profile-name">
              {isLoading ? (
                <ProfileNameSkeleton />
              ) : (
                <div className="profile-name-sec">
                  {userView.first_name ? (
                    <>
                      {firstName ? firstName : userView.first_name}{" "}
                      {middleName ? middleName : (userView.middle_name && userView.middle_name !== "null" ? userView.middle_name + " " : "")}
                      {lastName ? lastName : userView.last_name}{" "}
                    </>
                  ) : (
                    <>
                      {companyFirstName?.trim() || companyLastName?.trim() ? (
                        `${companyFirstName || ''} ${companyLastName || ''}`
                      ) : (
                        companyName ||
                        (userView.company_first_name ?
                          `${userView.company_first_name} ${userView.company_last_name || ''}`
                          : `${userView.company_name || ''} (Company)`
                        )
                      )}
                    </>
                  )}
                </div>
              )}
              {isLoading ? (
                <ProfileTypeSkeleton />
              ) : (
                <>
                  {userType ? (
                    userType === userTypeName.Super_Admin ? (
                      <div className="profile-type">
                        Super Admin
                      </div>
                    ) : userType === userTypeName.Admin ? (
                      <div className="profile-type">
                        Admin
                      </div>
                    ) : userType === userTypeName.User ? (
                      <div className="profile-type">
                        User
                      </div>
                    ) : null
                  ) : (
                    <>
                      {userView.role_id && (
                        <div className="profile-type">
                          {userView.role_id.user_type}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
              <div className="profile-status">
                {isLoading ? (
                  <StatusButtonSkeleton />
                ) : (
                  <>
                    {user && (user.userType === SUPER_ADMIN || user.userType === ADMIN) ? (
                      userView.is_active === false ? (
                        <div 
                          className="inactive" 
                          onClick={() => !isSubmitLoading ? StatusChangeInactive(userView._id) : undefined} 
                          style={{ 
                            cursor: isSubmitLoading ? "not-allowed" : "pointer",
                            pointerEvents: isSubmitLoading ? "none" : "auto",
                            opacity: isSubmitLoading ? 0.6 : 1,
                          }}
                        >
                          {isSubmitLoading ? (
                            <StatusButtonSkeleton />
                          ) : (
                            "Inactive"
                          )}
                        </div>
                      ) : userView.is_active === true ? (
                        <div 
                          className="active" 
                          onClick={() => !isSubmitLoading ? StatusChangeActive(userView._id) : undefined} 
                          style={{ 
                            cursor: isSubmitLoading ? "not-allowed" : "pointer",
                            pointerEvents: isSubmitLoading ? "none" : "auto",
                            opacity: isSubmitLoading ? 0.6 : 1,
                          }}
                        >
                          {isSubmitLoading ? (
                            <StatusButtonSkeleton />
                          ) : (
                            "Active"
                          )}
                        </div>
                      ) : null
                    ) : (
                      userView.is_active === false ? (
                        <div className="inactive">
                          {isSubmitLoading ? (
                            <StatusButtonSkeleton />
                          ) : (
                            "Inactive"
                          )}
                        </div>
                      ) : (
                        <div className="active">
                          {isSubmitLoading ? (
                            <StatusButtonSkeleton />
                          ) : (
                            "Active"
                          )}
                        </div>
                      )
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserProfileHeader;
